import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowLeft, Copy, ExternalLink, CheckCircle, ShieldCheck } from 'lucide-react';
import { authAPI } from '../lib/api';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTheme } from '../context/ThemeContext';

export default function AuthPage() {
    var authContext = useAuth();
    var navigate = useNavigate();
    var location = useLocation();
    var { resolvedTheme } = useTheme();
    var [loading, setLoading] = useState(false);
    var [activeTab, setActiveTab] = useState('login');
    var [loginData, setLoginData] = useState({ email: '', password: '' });
    var [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
    var [loginErrors, setLoginErrors] = useState({});
    var [registerErrors, setRegisterErrors] = useState({});
    
    // Forgot password state
    var [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    var [forgotEmail, setForgotEmail] = useState('');
    var [forgotLoading, setForgotLoading] = useState(false);
    var [resetLink, setResetLink] = useState(null);

    // 2FA state
    var [show2FA, setShow2FA] = useState(false);
    var [tempToken, setTempToken] = useState('');
    var [twoFACode, setTwoFACode] = useState('');
    var [turnstileToken, setTurnstileToken] = useState('');
    var turnstileRef = useRef(null);

    React.useEffect(function() {
        if (authContext.isAuthenticated) {
            var from = (location.state && location.state.from) || '/';
            navigate(from, { replace: true });
        }
    }, [authContext.isAuthenticated, navigate, location]);

    function parseErrorDetail(detail) {
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) {
            return detail.map(function(e) { return e.msg || JSON.stringify(e); }).join('. ');
        }
        return 'Hiba történt';
    }

    function validateEmail(email) {
        if (!email) return 'Add meg az email címed';
        var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(email)) return 'Érvénytelen email cím formátum';
        return '';
    }

    function handleLoginBlur(field) {
        return function() {
            var errs = Object.assign({}, loginErrors);
            delete errs.form;
            if (field === 'email') {
                var emailErr = validateEmail(loginData.email);
                if (emailErr) errs.email = emailErr; else delete errs.email;
            }
            if (field === 'password') {
                if (!loginData.password) errs.password = 'Add meg a jelszavad'; else delete errs.password;
            }
            setLoginErrors(errs);
        };
    }

    function handleRegisterBlur(field) {
        return function() {
            var errs = Object.assign({}, registerErrors);
            delete errs.form;
            if (field === 'name') {
                if (!registerData.name) errs.name = 'Add meg a neved'; else delete errs.name;
            }
            if (field === 'email') {
                var emailErr = validateEmail(registerData.email);
                if (emailErr) errs.email = emailErr; else delete errs.email;
            }
            if (field === 'password') {
                var pw = registerData.password;
                if (!pw) { errs.password = 'Add meg a jelszavad'; }
                else if (pw.length < 8) { errs.password = 'A jelszónak legalább 8 karakter hosszúnak kell lennie'; }
                else if (!/[A-Z]/.test(pw)) { errs.password = 'A jelszónak tartalmaznia kell legalább egy nagybetűt'; }
                else if (!/[a-z]/.test(pw)) { errs.password = 'A jelszónak tartalmaznia kell legalább egy kisbetűt'; }
                else if (!/\d/.test(pw)) { errs.password = 'A jelszónak tartalmaznia kell legalább egy számot'; }
                else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw)) { errs.password = 'A jelszónak tartalmaznia kell legalább egy speciális karaktert'; }
                else { delete errs.password; }
            }
            if (field === 'confirmPassword') {
                if (registerData.password && registerData.confirmPassword && registerData.password !== registerData.confirmPassword) {
                    errs.confirmPassword = 'A jelszavak nem egyeznek';
                } else { delete errs.confirmPassword; }
            }
            setRegisterErrors(errs);
        };
    }

    function handleLogin(e) {
        e.preventDefault();
        var errs = {};
        var emailErr = validateEmail(loginData.email);
        if (emailErr) errs.email = emailErr;
        if (!loginData.password) errs.password = 'Add meg a jelszavad';
        if (process.env.REACT_APP_TURNSTILE_SITEKEY && !turnstileToken) errs.captcha = 'Kérlek végezd el a CAPTCHA ellenőrzést';
        setLoginErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setLoading(true);
        authContext.login(loginData.email, loginData.password, turnstileToken)
            .then(function(result) {
                if (result && result.requires_2fa) {
                    setTempToken(result.temp_token);
                    setShow2FA(true);
                    setLoading(false);
                    return;
                }
                toast.success('Üdvözlünk újra!');
                var from = (location.state && location.state.from) || '/';
                navigate(from, { replace: true });
            })
            .catch(function(error) {
                var detail = (error.response && error.response.data && error.response.data.detail) || 'Sikertelen bejelentkezés';
                detail = parseErrorDetail(detail);
                if (error.response && error.response.status === 403 && typeof detail === 'string' && detail.includes('email')) {
                    setVerificationEmail(loginData.email);
                    setShowVerificationMessage(true);
                    setActiveTab('register');
                }
                setLoginErrors({ form: detail });
                setTurnstileToken('');
                if (turnstileRef.current) turnstileRef.current.reset();
            })
            .finally(function() { setLoading(false); });
    }

    function handle2FASubmit(e) {
        e.preventDefault();
        if (!twoFACode || twoFACode.length !== 6) { toast.error('Kérlek add meg a 6 jegyű kódot'); return; }
        setLoading(true);
        authContext.verify2FA(tempToken, twoFACode)
            .then(function() {
                toast.success('Sikeres bejelentkezés!');
                var from = (location.state && location.state.from) || '/';
                navigate(from, { replace: true });
            })
            .catch(function(error) { toast.error((error.response && error.response.data && error.response.data.detail) || 'Hibás hitelesítési kód'); })
            .finally(function() { setLoading(false); });
    }

    // Registration verification state
    var [showVerificationMessage, setShowVerificationMessage] = useState(false);
    var [verificationEmail, setVerificationEmail] = useState('');
    var [registerTurnstileToken, setRegisterTurnstileToken] = useState('');
    var registerTurnstileRef = useRef(null);

    function handleRegister(e) {
        e.preventDefault();
        var errs = {};
        if (!registerData.name) errs.name = 'Add meg a neved';
        if (!registerData.email) errs.email = 'Add meg az email címed';
        if (!registerData.password) errs.password = 'Add meg a jelszavad';
        if (registerData.password !== registerData.confirmPassword) errs.confirmPassword = 'A jelszavak nem egyeznek';
        if (process.env.REACT_APP_TURNSTILE_SITEKEY && !registerTurnstileToken) errs.captcha = 'Kérlek végezd el a CAPTCHA ellenőrzést';
        var pw = registerData.password;
        if (pw && pw.length < 8) errs.password = 'A jelszónak legalább 8 karakter hosszúnak kell lennie';
        else if (pw && !/[A-Z]/.test(pw)) errs.password = 'A jelszónak tartalmaznia kell legalább egy nagybetűt';
        else if (pw && !/[a-z]/.test(pw)) errs.password = 'A jelszónak tartalmaznia kell legalább egy kisbetűt';
        else if (pw && !/\d/.test(pw)) errs.password = 'A jelszónak tartalmaznia kell legalább egy számot';
        else if (pw && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw)) errs.password = 'A jelszónak tartalmaznia kell legalább egy speciális karaktert';
        setRegisterErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setLoading(true);
        authContext.register(registerData.name, registerData.email, registerData.password, registerTurnstileToken)
            .then(function(result) {
                if (result && result.requires_verification) {
                    setVerificationEmail(registerData.email);
                    setShowVerificationMessage(true);
                    toast.success('Regisztráció sikeres! Ellenőrizd az email fiókod.');
                } else {
                    toast.success('Sikeres regisztráció!');
                    var from = (location.state && location.state.from) || '/';
                    navigate(from, { replace: true });
                }
            })
            .catch(function(error) { 
                var detail = (error.response && error.response.data && error.response.data.detail) || 'Sikertelen regisztráció';
                setRegisterErrors({ form: parseErrorDetail(detail) });
                if (registerTurnstileRef.current) registerTurnstileRef.current.reset();
                setRegisterTurnstileToken('');
            })
            .finally(function() { setLoading(false); });
    }
    
    var [forgotError, setForgotError] = useState('');

    function handleForgotPassword(e) {
        e.preventDefault();
        if (!forgotEmail) { setForgotError('Kérlek add meg az email címed'); return; }
        setForgotError('');
        setForgotLoading(true);
        setResetLink(null);
        
        authAPI.forgotPassword(forgotEmail)
            .then(function(response) {
                setResetLink('sent');
                toast.success('Jelszó-visszaállítási link elküldve!');
            })
            .catch(function(error) {
                var detail = (error.response && error.response.data && error.response.data.detail) || 'Hiba történt';
                setForgotError(parseErrorDetail(detail));
            })
            .finally(function() { setForgotLoading(false); });
    }
    
    function copyResetLink() {
        var fullLink = window.location.origin + resetLink;
        navigator.clipboard.writeText(fullLink);
        toast.success('Link vágólapra másolva!');
    }
    
    function goToResetPage() {
        navigate(resetLink);
        setForgotPasswordOpen(false);
    }

    return (
        <div className="min-h-screen flex" data-testid="auth-page">
            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
                <div className="w-full max-w-md">
                    <div className="flex items-center mb-8">
                        <img src={resolvedTheme === 'dark' ? "/logo-dark.svg" : "/logo.svg"} alt="NexusRig" className="h-24 w-auto" />
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full mb-8">
                            <TabsTrigger value="login" className="flex-1">Bejelentkezés</TabsTrigger>
                            <TabsTrigger value="register" className="flex-1">Regisztráció</TabsTrigger>
                        </TabsList>

                        <TabsContent value="login">
                            <div className="space-y-2 mb-6">
                                <h1 className="font-heading text-3xl font-bold">Üdvözlünk újra</h1>
                                <p className="text-muted-foreground">Add meg az adataidat a belépéshez</p>
                            </div>
                            <form onSubmit={handleLogin} className="space-y-4">
                                {loginErrors.form && (
                                    <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg px-4 py-3" data-testid="login-form-error">{loginErrors.form}</div>
                                )}
                                <div>
                                    <Label htmlFor="login-email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="login-email" type="email" placeholder="nev@pelda.hu" className={`pl-10 ${loginErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={loginData.email} onBlur={handleLoginBlur('email')} onChange={function(e) { setLoginErrors(function(prev) { var n = Object.assign({}, prev); delete n.email; delete n.form; return n; }); setLoginData(function(prev) { return Object.assign({}, prev, { email: e.target.value }); }); }} data-testid="login-email-input" />
                                    </div>
                                    {loginErrors.email && <p className="text-xs text-red-500 mt-1" data-testid="login-email-error">{loginErrors.email}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="login-password">Jelszó</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="login-password" type="password" placeholder="••••••••" className={`pl-10 ${loginErrors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={loginData.password} onBlur={handleLoginBlur('password')} onChange={function(e) { setLoginErrors(function(prev) { var n = Object.assign({}, prev); delete n.password; delete n.form; return n; }); setLoginData(function(prev) { return Object.assign({}, prev, { password: e.target.value }); }); }} data-testid="login-password-input" />
                                    </div>
                                    {loginErrors.password && <p className="text-xs text-red-500 mt-1" data-testid="login-password-error">{loginErrors.password}</p>}
                                </div>
                                <div className="flex justify-end">
                                    <button 
                                        type="button" 
                                        onClick={function() { setForgotPasswordOpen(true); setForgotEmail(loginData.email); setResetLink(null); }}
                                        className="text-sm text-primary hover:underline"
                                        data-testid="forgot-password-link"
                                    >
                                        Elfelejtett jelszó?
                                    </button>
                                </div>
                                {process.env.REACT_APP_TURNSTILE_SITEKEY && (
                                    <div className="flex justify-center my-3">
                                        <Turnstile
                                            key={resolvedTheme}
                                            ref={turnstileRef}
                                            siteKey={process.env.REACT_APP_TURNSTILE_SITEKEY}
                                            onSuccess={setTurnstileToken}
                                            onExpire={() => setTurnstileToken('')}
                                            options={{ theme: resolvedTheme === 'dark' ? 'dark' : 'light', size: 'normal' }}
                                        />
                                    </div>
                                )}
                                <Button type="submit" className="w-full btn-glow" disabled={loading || (process.env.REACT_APP_TURNSTILE_SITEKEY && !turnstileToken)} data-testid="login-submit-button">
                                    {loading ? 'Bejelentkezés...' : 'Belépés'}
                                </Button>
                            </form>
                        </TabsContent>

                        <TabsContent value="register">
                            {showVerificationMessage ? (
                                <div className="space-y-6 text-center py-8">
                                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                                        <Mail className="w-8 h-8 text-primary" />
                                    </div>
                                    <div className="space-y-2">
                                        <h2 className="font-heading text-2xl font-bold">Ellenőrizd az emailjeidet!</h2>
                                        <p className="text-muted-foreground">Küldtünk egy megerősítő linket a következő címre:</p>
                                        <p className="font-medium text-primary">{verificationEmail}</p>
                                    </div>
                                    <p className="text-sm text-muted-foreground">Kattints a linkre az emailben a fiókod aktiválásához. A link 24 órán belül lejár.</p>
                                    <div className="space-y-3">
                                        <Button variant="outline" className="w-full" onClick={function() {
                                            authAPI.resendVerification(verificationEmail)
                                                .then(function() { toast.success('Megerősítő email újraküldve!'); })
                                                .catch(function() { toast.error('Hiba az email küldésekor'); });
                                        }} data-testid="resend-verification-btn">Email újraküldése</Button>
                                        <Button variant="ghost" className="w-full" onClick={function() { setShowVerificationMessage(false); setActiveTab('login'); }} data-testid="back-to-login-btn">Vissza a bejelentkezéshez</Button>
                                    </div>
                                </div>
                            ) : (
                            <>
                            <div className="space-y-2 mb-6">
                                <h1 className="font-heading text-3xl font-bold">Fiók Létrehozása</h1>
                                <p className="text-muted-foreground">Add meg az adataidat a regisztrációhoz</p>
                            </div>
                            <form onSubmit={handleRegister} className="space-y-4">
                                {registerErrors.form && (
                                    <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg px-4 py-3" data-testid="register-form-error">{registerErrors.form}</div>
                                )}
                                <div>
                                    <Label htmlFor="register-name">Teljes Név</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="register-name" placeholder="Kovács János" className={`pl-10 ${registerErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={registerData.name} onBlur={handleRegisterBlur('name')} onChange={function(e) { setRegisterErrors(function(prev) { var n = Object.assign({}, prev); delete n.name; delete n.form; return n; }); setRegisterData(function(prev) { return Object.assign({}, prev, { name: e.target.value }); }); }} data-testid="register-name-input" />
                                    </div>
                                    {registerErrors.name && <p className="text-xs text-red-500 mt-1" data-testid="register-name-error">{registerErrors.name}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="register-email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="register-email" type="email" placeholder="nev@pelda.hu" className={`pl-10 ${registerErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={registerData.email} onBlur={handleRegisterBlur('email')} onChange={function(e) { setRegisterErrors(function(prev) { var n = Object.assign({}, prev); delete n.email; delete n.form; return n; }); setRegisterData(function(prev) { return Object.assign({}, prev, { email: e.target.value }); }); }} data-testid="register-email-input" />
                                    </div>
                                    {registerErrors.email && <p className="text-xs text-red-500 mt-1" data-testid="register-email-error">{registerErrors.email}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="register-password">Jelszó</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="register-password" type="password" placeholder="••••••••" className={`pl-10 ${registerErrors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={registerData.password} onBlur={handleRegisterBlur('password')} onChange={function(e) { setRegisterErrors(function(prev) { var n = Object.assign({}, prev); delete n.password; delete n.form; return n; }); setRegisterData(function(prev) { return Object.assign({}, prev, { password: e.target.value }); }); }} data-testid="register-password-input" />
                                    </div>
                                    {registerErrors.password && <p className="text-xs text-red-500 mt-1" data-testid="register-password-error">{registerErrors.password}</p>}
                                    {registerData.password && (
                                        <PasswordStrength password={registerData.password} />
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="register-confirm">Jelszó Megerősítése</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input id="register-confirm" type="password" placeholder="••••••••" className={`pl-10 ${registerErrors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`} value={registerData.confirmPassword} onBlur={handleRegisterBlur('confirmPassword')} onChange={function(e) { setRegisterErrors(function(prev) { var n = Object.assign({}, prev); delete n.confirmPassword; delete n.form; return n; }); setRegisterData(function(prev) { return Object.assign({}, prev, { confirmPassword: e.target.value }); }); }} data-testid="register-confirm-input" />
                                    </div>
                                    {registerErrors.confirmPassword && <p className="text-xs text-red-500 mt-1" data-testid="register-confirm-error">{registerErrors.confirmPassword}</p>}
                                </div>
                                {process.env.REACT_APP_TURNSTILE_SITEKEY && (
                                    <div className="flex justify-center">
                                        <Turnstile
                                            ref={registerTurnstileRef}
                                            siteKey={process.env.REACT_APP_TURNSTILE_SITEKEY}
                                            onSuccess={setRegisterTurnstileToken}
                                            onError={function() { setRegisterTurnstileToken(''); }}
                                            onExpire={function() { setRegisterTurnstileToken(''); }}
                                            options={{ theme: resolvedTheme === 'dark' ? 'dark' : 'light', size: 'normal' }}
                                        />
                                    </div>
                                )}
                                <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="register-submit-button">
                                    {loading ? 'Regisztráció...' : 'Fiók Létrehozása'}
                                </Button>
                            </form>
                            </>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            <div className="hidden lg:block lg:w-1/2 relative">
                <div className="absolute inset-0 bg-gradient-to-l from-background/0 to-background z-10" />
                <img src="/auth_bg.jpg" alt="Gaming Setup" className="w-full h-full object-cover" />
            </div>
            
            {/* 2FA Verification Dialog */}
            <Dialog open={show2FA} onOpenChange={function(open) { if (!open) { setShow2FA(false); setTwoFACode(''); } }}>
                <DialogContent className="sm:max-w-md" data-testid="2fa-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                            Kétfaktoros hitelesítés
                        </DialogTitle>
                        <DialogDescription>
                            Add meg a hitelesítő alkalmazás által generált 6 jegyű kódot.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handle2FASubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="2fa-code">Hitelesítési kód</Label>
                            <Input
                                id="2fa-code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="000000"
                                className="text-center text-2xl tracking-[0.5em] font-mono"
                                value={twoFACode}
                                onChange={function(e) { setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
                                autoFocus
                                data-testid="2fa-code-input"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={function() { setShow2FA(false); setTwoFACode(''); }} className="flex-1">
                                Mégse
                            </Button>
                            <Button type="submit" disabled={loading || twoFACode.length !== 6} className="flex-1" data-testid="2fa-submit-button">
                                {loading ? 'Ellenőrzés...' : 'Belépés'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Forgot Password Dialog */}
            <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
                <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
                    <DialogHeader>
                        <DialogTitle>Elfelejtett jelszó</DialogTitle>
                        <DialogDescription>
                            Add meg az email címed és küldünk egy jelszó-visszaállítási linket.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!resetLink ? (
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                            {forgotError && (
                                <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg px-4 py-3">{forgotError}</div>
                            )}
                            <div>
                                <Label htmlFor="forgot-email">Email cím</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input 
                                        id="forgot-email" 
                                        type="email" 
                                        placeholder="nev@pelda.hu" 
                                        className={`pl-10 ${forgotError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                        value={forgotEmail} 
                                        onChange={function(e) { setForgotError(''); setForgotEmail(e.target.value); }}
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button type="button" variant="outline" onClick={function() { setForgotPasswordOpen(false); }} className="flex-1">
                                    Mégse
                                </Button>
                                <Button type="submit" disabled={forgotLoading} className="flex-1" data-testid="forgot-submit-button">
                                    {forgotLoading ? 'Küldés...' : 'Link küldése'}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-green-500 mb-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-medium">Email elküldve!</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Ha a megadott email cím létezik a rendszerünkben, küldtünk egy jelszó-visszaállítási linket a <strong>{forgotEmail}</strong> címre.
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Kérjük ellenőrizd a postaládádat (és a spam mappát is). A link 1 órán belül lejár.
                            </p>
                            <Button variant="outline" onClick={function() { setForgotPasswordOpen(false); setResetLink(null); }} className="w-full" data-testid="forgot-close-button">
                                Bezárás
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PasswordStrength({ password }) {
    var checks = [
        { label: 'Min. 8 karakter', met: password.length >= 8 },
        { label: 'Nagybetű (A-Z)', met: /[A-Z]/.test(password) },
        { label: 'Kisbetű (a-z)', met: /[a-z]/.test(password) },
        { label: 'Szám (0-9)', met: /\d/.test(password) },
        { label: 'Speciális karakter', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password) },
    ];
    var metCount = checks.filter(function(c) { return c.met; }).length;
    var strength = metCount <= 2 ? 'Gyenge' : metCount <= 4 ? 'Közepes' : 'Erős';
    var color = metCount <= 2 ? 'bg-red-500' : metCount <= 4 ? 'bg-amber-500' : 'bg-emerald-500';
    var textColor = metCount <= 2 ? 'text-red-400' : metCount <= 4 ? 'text-amber-400' : 'text-emerald-400';

    return (
        <div className="mt-2 space-y-2" data-testid="password-strength">
            <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all duration-300`} style={{ width: (metCount / 5 * 100) + '%' }} />
                </div>
                <span className={`text-xs font-medium ${textColor}`}>{strength}</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
                {checks.map(function(c) {
                    return (
                        <span key={c.label} className={`text-[11px] ${c.met ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                            {c.met ? '\u2713' : '\u2022'} {c.label}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}
